/**
 * ResearchPanel — enhanced thesis research with charts, enrichment, and buy.
 * User types a free-text thesis; we call /api/v1/research/thesis and render:
 * - Enriched candidates table (P/E, market cap, cap type, info link)
 * - Performance chart (Plotly) vs SPY and QQQ
 * - Expected returns table (historical + forward)
 * - One-click buy button
 * - Saved research sidebar
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../lib/api';
import { useAuth } from '../lib/auth';
import { useFocal } from '../lib/focal';
import { useToast } from '../lib/toast';
import { PerformanceChart } from './PerformanceChart';
import { BuyThesisDialog } from './BuyThesisDialog';
import { SavedResearchList } from './SavedResearchList';

export function ResearchPanel({ initialQuery, autoRun }: { initialQuery?: string; autoRun?: boolean }) {
  const { set } = useFocal();
  const navigate = useNavigate();
  const toast = useToast();
  const { me } = useAuth();
  const isLiveMode = me?.mode === 'live';
  const [query, setQuery] = useState(initialQuery ?? '');
  const [depth, setDepth] = useState(8);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<any | null>(null);
  const [showBuy, setShowBuy] = useState<false | 'sim' | 'live'>(false);
  const [buyResult, setBuyResult] = useState<any | null>(null);
  const [trackers, setTrackers] = useState<any[]>([]);

  async function run() {
    if (!query.trim()) return;
    setBusy(true);
    setErr(null);
    setData(null);
    setBuyResult(null);
    try {
      setData(await api.research.thesis(query.trim(), depth));
    } catch (e: unknown) {
      setErr((e as Error).message ?? 'failed');
    } finally {
      setBusy(false);
    }
  }

  function loadSession(session: any) {
    setData(session.result || session);
    setQuery(session.query || '');
  }

  useEffect(() => {
    api.research.thesisPerformance().then((d) => setTrackers(d.trackers || [])).catch(() => {});
  }, []);

  // v0.5.4 AE-066: when arriving via `/research?q=…` (FocalCard / chat
  // / morning email deeplink), auto-run the thesis pipeline once. Without
  // this the user lands on a pre-filled textarea but still has to click
  // "develop thesis" — the deeplink was half-honored.
  const didAutoRunRef = useRef(false);
  useEffect(() => {
    if (!autoRun || didAutoRunRef.current) return;
    if (!initialQuery || !initialQuery.trim()) return;
    didAutoRunRef.current = true;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, initialQuery]);

  const rankedSymbols = (data?.fundamental_ranking?.results ?? []).map((r: any) => r.symbol);
  const enrichment = data?.enrichment || {};
  const equityCurve = enrichment?.historical_return?.equity_curve ?? [];

  return (
    <div className="space-y-3 max-w-4xl">
      <SavedResearchList
        onLoad={loadSession}
        onModify={(s) => { setQuery(s.query); setDepth(s.depth); }}
      />

      {trackers.length > 0 && <ThesisTrackerCard trackers={trackers} />}

      <div className="card p-3 grid grid-cols-[1fr_100px_auto] gap-2 items-end">
        <label className="block">
          <span className="text-[11px] text-ink-mute">thesis (free text)</span>
          <textarea
            className="input w-full mt-1 resize-y"
            rows={2}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='e.g. "B2B agentic AI growth", "small-cap energy turnaround"'
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); run(); } }}
          />
        </label>
        <label className="block">
          <span className="text-[11px] text-ink-mute">universe size</span>
          <input
            type="number"
            className="input w-full mt-1 num"
            min={3}
            max={20}
            value={depth}
            onChange={(e) => setDepth(Number(e.target.value))}
          />
        </label>
        <button className="btn-primary" onClick={run} disabled={busy || !query.trim()}>
          {busy ? 'researching…' : 'develop thesis'}
        </button>
      </div>

      {err && <div className="text-bad text-xs">{err}</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <SeedCard data={data} />
            <MacroCard data={data} />
          </div>
          <ThemeCard data={data} />
          <EnrichedRankingCard
            data={data}
            enrichment={enrichment}
            onPick={(sym) => set({ kind: 'chart', symbol: sym })}
          />
          <SentimentCard data={data} />

          {equityCurve.length > 2 && (
            <PerformanceChart equityCurve={equityCurve} />
          )}

          <ExpectedReturnsTable enrichment={enrichment} />

          <div className="flex items-center gap-3 flex-wrap" data-testid="buy-buttons">
            <button
              className="btn-primary bg-emerald-600 hover:bg-emerald-700 text-sm px-5 py-2"
              onClick={() => setShowBuy('sim')}
              disabled={rankedSymbols.length === 0}
              data-testid="buy-sim-button"
              title="Always paper-fills via SimBroker (ledger persists in DB)"
            >
              Buy SIM
            </button>
            <button
              className={
                isLiveMode
                  ? 'btn-primary text-sm px-5 py-2 bg-warn text-bg hover:opacity-90'
                  : 'btn text-sm px-5 py-2 opacity-50 cursor-not-allowed'
              }
              onClick={() => isLiveMode && setShowBuy('live')}
              disabled={rankedSymbols.length === 0 || !isLiveMode}
              data-testid="buy-live-button"
              title={
                isLiveMode
                  ? 'Routes to your real Schwab account (queues pending_approval first)'
                  : 'Switch to LIVE mode (top bar) to enable real-money orders'
              }
            >
              Buy LIVE
            </button>
            {!isLiveMode && (
              <span className="text-[11px] text-ink-mute" data-testid="buy-live-hint">
                Buy LIVE disabled — switch to LIVE mode in the top bar
              </span>
            )}
            {buyResult && (
              <span className="text-xs text-ok flex items-center gap-2" data-testid="buy-result">
                {buyResult.orders_placed?.length ?? 0} placed, {buyResult.orders_pending?.length ?? 0} pending approval
                {buyResult.thesis_id && (
                  <button
                    className="underline hover:opacity-80"
                    onClick={() => navigate(`/portfolio?view=theses&thesis_id=${buyResult.thesis_id}`)}
                  >
                    View in Portfolio →
                  </button>
                )}
              </span>
            )}
          </div>

          {showBuy && (
            <BuyThesisDialog
              symbols={rankedSymbols.slice(0, 5)}
              weights={rankedSymbols.slice(0, 5).map(() => 1.0 / Math.min(rankedSymbols.length, 5))}
              researchSessionId={data?.research_session_id}
              buyMode={showBuy}
              onClose={() => setShowBuy(false)}
              onSuccess={(r) => {
                setBuyResult(r);
                setShowBuy(false);
                const placedCount = r.orders_placed?.length ?? 0;
                const pendingCount = r.orders_pending?.length ?? 0;
                const modeLabel = r.mode === 'live' ? 'LIVE' : 'SIM';
                toast.show(
                  `Buy ${modeLabel}: ${placedCount} placed, ${pendingCount} pending approval`,
                  'ok',
                  {
                    action: r.thesis_id
                      ? {
                          label: 'View in Portfolio',
                          onClick: () =>
                            navigate(`/portfolio?view=theses&thesis_id=${r.thesis_id}`),
                        }
                      : undefined,
                  },
                );
              }}
            />
          )}

          <div className="text-[11px] text-ink-mute">
            Research auto-saved. All orders go through the approval framework (Layer 11).
          </div>
        </>
      )}
    </div>
  );
}

function SeedCard({ data }: { data: any }) {
  const seed = data.universe_seed;
  return (
    <div className="card p-3">
      <div className="text-[11px] text-ink-mute uppercase mb-2">universe seed</div>
      <div className="text-xs font-semibold mb-1">{seed?.universe?.length ?? 0} candidates</div>
      <div className="text-[11px] num text-ink-mute break-words">
        {(seed?.universe ?? []).join(', ') || '—'}
      </div>
      {seed?.theme_keywords?.length ? (
        <div className="mt-2 text-[11px] text-ink-mute">
          keywords: {seed.theme_keywords.join(' · ')}
        </div>
      ) : null}
    </div>
  );
}

function MacroCard({ data }: { data: any }) {
  const m = data.macro_backdrop || {};
  const ff = (m.fed_funds_target_upper as any) || {};
  const ten = (m.treasury_10y as any) || {};
  const yc = (m.yield_curve_2_10 as any) || {};
  const cpi = (m.cpi as any) || {};
  const vix = (m.vix as any) || {};
  return (
    <div className="card p-3">
      <div className="text-[11px] text-ink-mute uppercase mb-2">macro backdrop</div>
      <table className="w-full text-xs num">
        <tbody>
          <Row label="FedFunds upper" v={ff.latest} />
          <Row label="10Y Treasury" v={ten.latest} />
          <Row label="2-10 spread" v={yc.latest} />
          <Row label="CPI YoY %" v={cpi.yoy_pct} />
          <Row label="VIX" v={vix.latest} />
        </tbody>
      </table>
    </div>
  );
}

function Row({ label, v }: { label: string; v: number | undefined }) {
  return (
    <tr>
      <td className="text-ink-mute">{label}</td>
      <td className="text-right">{v ?? '—'}</td>
    </tr>
  );
}

function ThemeCard({ data }: { data: any }) {
  const t = (data.theme_momentum || {}) as any;
  if (!t.theme && !t.verdict) return null;
  return (
    <div className="card p-3 text-xs">
      <div className="text-[11px] text-ink-mute uppercase mb-1">theme momentum</div>
      <div>
        {String(t.theme)} — <span className="font-semibold">{String(t.verdict ?? '')}</span>
        {t.n_items != null && <span className="text-ink-mute ml-2">{t.n_items} items</span>}
        {t.engagement_score != null && <span className="text-ink-mute ml-2">engagement {t.engagement_score}</span>}
      </div>
    </div>
  );
}

function EnrichedRankingCard({
  data,
  enrichment,
  onPick,
}: {
  data: any;
  enrichment: any;
  onPick: (sym: string) => void;
}) {
  const rows = data.fundamental_ranking?.results ?? [];
  const candidates = enrichment?.candidates ?? [];
  const enrichMap: Record<string, any> = {};
  for (const c of candidates) {
    enrichMap[c.symbol] = c;
  }

  if (rows.length === 0) {
    return (
      <div className="card p-3 text-xs text-ink-mute">
        No fundamentals could be fetched.
      </div>
    );
  }
  return (
    <div className="card p-3 overflow-x-auto">
      <div className="text-[11px] text-ink-mute uppercase mb-2">
        ranked candidates ({rows.length} passed)
      </div>
      <table className="w-full text-xs num">
        <thead className="text-ink-mute text-[11px]">
          <tr>
            <th className="text-left py-1">symbol</th>
            <th className="text-left py-1">name</th>
            <th className="text-right py-1">score</th>
            <th className="text-right py-1">P/E</th>
            <th className="text-right py-1">mkt cap</th>
            <th className="text-left py-1">cap type</th>
            <th className="text-right py-1">rev</th>
            <th className="text-right py-1">NI</th>
            <th className="text-right py-1">D/A</th>
            <th className="text-center py-1">info</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r: any) => {
            const e = enrichMap[r.symbol] || {};
            return (
              <tr key={r.symbol} className="border-t border-edge">
                <td className="py-1 font-semibold">
                  <button
                    className="text-accent hover:underline"
                    onClick={() => onPick(r.symbol)}
                    title="open chart"
                  >
                    {r.symbol}
                  </button>
                </td>
                <td className="text-ink-mute truncate max-w-[120px]">{r.name ?? ''}</td>
                <td className="text-right">{r.score?.toFixed(2) ?? '—'}</td>
                <td className="text-right">{e.pe_ratio?.toFixed(1) ?? '—'}</td>
                <td className="text-right">{fmtBig(e.market_cap_usd)}</td>
                <td className="text-left">
                  {e.cap_type && (
                    <span className={`text-[10px] px-1 py-0.5 rounded ${capColor(e.cap_type)}`}>
                      {e.cap_type}
                    </span>
                  )}
                </td>
                <td className="text-right">{fmtBig(r.revenue)}</td>
                <td className="text-right">{fmtBig(r.net_income)}</td>
                <td className="text-right">{r.debt_to_assets ?? '—'}</td>
                <td className="text-center">
                  {e.info_url && (
                    <a href={e.info_url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline text-[10px]">
                      ↗
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ExpectedReturnsTable({ enrichment }: { enrichment: any }) {
  const hist = enrichment?.historical_return;
  const fwd = enrichment?.forward_expectation;
  if (!hist && !fwd) return null;

  return (
    <div className="card p-3">
      <div className="text-[11px] text-ink-mute uppercase mb-2">expected returns</div>
      <table className="w-full text-xs num">
        <thead className="text-ink-mute text-[11px]">
          <tr>
            <th className="text-left py-1">method</th>
            <th className="text-right py-1">annual return</th>
            <th className="text-right py-1">sharpe</th>
            <th className="text-right py-1">max DD</th>
            <th className="text-right py-1">alpha vs SPY</th>
          </tr>
        </thead>
        <tbody>
          {hist && (
            <tr className="border-t border-edge">
              <td className="py-1">Historical ({Math.round((hist.lookback_days || 0) / 252)}yr)</td>
              <td className="text-right">{hist.annualized_return_pct?.toFixed(1)}%</td>
              <td className="text-right">{hist.sharpe?.toFixed(2) ?? '—'}</td>
              <td className="text-right">{hist.max_drawdown_pct?.toFixed(1)}%</td>
              <td className="text-right">{hist.vs_spy_alpha_pct != null ? `${hist.vs_spy_alpha_pct > 0 ? '+' : ''}${hist.vs_spy_alpha_pct.toFixed(1)}%` : '—'}</td>
            </tr>
          )}
          {fwd && (
            <tr className="border-t border-edge">
              <td className="py-1">Forward ({fwd.source_mix?.options_implied ? 'options-implied' : 'momentum'})</td>
              <td className="text-right">{fwd.portfolio_implied_return_pct?.toFixed(1)}%</td>
              <td className="text-right text-ink-mute">—</td>
              <td className="text-right text-ink-mute">—</td>
              <td className="text-right text-ink-mute">
                confidence: {(fwd.portfolio_confidence * 100)?.toFixed(0)}%
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SentimentCard({ data }: { data: any }) {
  const rows = data.per_name_sentiment ?? [];
  if (rows.length === 0) return null;
  return (
    <div className="card p-3">
      <div className="text-[11px] text-ink-mute uppercase mb-2">per-name news sentiment (top 5)</div>
      <ul className="space-y-1 text-xs">
        {rows.map(({ symbol, sentiment }: any) => {
          const s = sentiment as any;
          return (
            <li key={symbol} className="flex items-baseline gap-2">
              <span className="font-semibold w-12">{symbol}</span>
              <span
                className={
                  (s?.label === 'bullish' && 'text-ok') ||
                  (s?.label === 'bearish' && 'text-bad') ||
                  'text-ink-mute'
                }
              >
                {String(s?.label ?? 'neutral')}
              </span>
              <span className="num text-ink-mute text-[11px]">
                {s?.score != null ? `(${Number(s.score).toFixed(2)})` : ''}
              </span>
              <span className="text-ink-dim truncate">{String(s?.summary ?? '')}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function capColor(cap: string): string {
  // v0.5.4 AE-072: dark-theme tokens. The light-mode hex palette
  // (bg-blue-100 / bg-amber-100 / bg-slate-100) clashed with bg-bg.
  // Use the semantic accent/warn/edge classes so cap-type pills are
  // legible on both light and dark themes.
  if (cap === 'mega-cap' || cap === 'large-cap') return 'bg-accent/15 text-accent';
  if (cap === 'mid-cap') return 'bg-warn/15 text-warn';
  return 'bg-edge/40 text-ink-mute';
}

function ThesisTrackerCard({ trackers }: { trackers: any[] }) {
  return (
    <div className="card p-3">
      <div className="text-[11px] text-ink-mute uppercase mb-2">active thesis trackers</div>
      <table className="w-full text-xs num">
        <thead className="text-ink-mute text-[11px]">
          <tr>
            <th className="text-left py-1">thesis</th>
            <th className="text-right py-1">invested</th>
            <th className="text-right py-1">current</th>
            <th className="text-right py-1">return</th>
            <th className="text-right py-1">entry</th>
          </tr>
        </thead>
        <tbody>
          {trackers.map((t) => (
            <tr key={t.id} className="border-t border-edge">
              <td className="py-1 truncate max-w-[150px]">{t.thesis_name}</td>
              <td className="text-right">{fmtBig(t.total_invested_usd)}</td>
              <td className="text-right">{t.current_value_usd ? fmtBig(t.current_value_usd) : '—'}</td>
              <td className={`text-right font-semibold ${
                t.return_pct > 0 ? 'text-ok' : t.return_pct < 0 ? 'text-bad' : ''
              }`}>
                {t.return_pct != null ? `${t.return_pct > 0 ? '+' : ''}${t.return_pct.toFixed(1)}%` : '—'}
              </td>
              <td className="text-right text-ink-mute">{new Date(t.entry_date).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmtBig(v: number | undefined | null): string {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
}
